import React from 'react';
import { ChevronLeft, Check, X, AlertTriangle } from 'lucide-react';

interface GuidelinesViewProps {
  setCurrentView: (view: string) => void;
}

const GuidelinesView: React.FC<GuidelinesViewProps> = ({ setCurrentView }) => {
  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <button 
          className="flex items-center text-indigo-600 font-medium mb-6 hover:text-indigo-800"
          onClick={() => setCurrentView('main')}
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to discussions
        </button>
        
        {/* Guidelines content */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">LSAT Community Guidelines</h1>
            
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 mb-6">
                Our community is built on mutual respect and collaborative learning. Following these guidelines ensures a 
                productive environment for everyone.
              </p>
              
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Respect and Support</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Be respectful of all community members regardless of their experience level or background.</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Provide constructive feedback that helps others learn and improve.</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Celebrate others' successes and offer encouragement during challenges.</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Avoid dismissive, condescending, or judgmental comments.</span>
                </li>
              </ul>
              
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Quality Content</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Use descriptive titles that clearly indicate your discussion topic.</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Post in the appropriate category to help others find relevant discussions.</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Check existing discussions before creating a new one on the same topic.</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Avoid vague questions without context or one-word responses that don't add value.</span>
                </li>
              </ul>
              
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Intellectual Property and Test Integrity</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Discuss concepts, strategies, and reasoning techniques freely.</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Cite sources when discussing published materials or quoting others' work.</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Do not share specific questions from official LSAT exams or licensed prep materials.</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Do not request or share answers to specific test questions or assignments.</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Discussions about specific question types or general patterns are permitted, but not verbatim reproduction of test content.</span>
                </li>
              </ul>
              
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Personal Information</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Use a username that doesn't reveal your full real name.</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2 mt-1" />
                  <span>You may choose to share general information about your study journey.</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Do not share contact information publicly - use the private messaging system instead.</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-500 flex-shrink-0 mr-2 mt-1" />
                  <span>Never share others' personal information without explicit permission.</span>
                </li>
              </ul>
              
              <div className="bg-indigo-50 p-6 rounded-lg mt-8">
                <h3 className="text-lg font-bold text-indigo-800 mb-3">Community Moderation</h3>
                <p className="text-indigo-900 mb-3">
                  Our community is moderated by volunteers and staff to ensure these guidelines are followed.
                  Repeated violations may result in temporary or permanent loss of posting privileges.
                </p>
                <p className="text-indigo-900">
                  If you see content that violates these guidelines, please report it using the "Report" button.
                  Thank you for helping maintain our positive learning environment!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidelinesView;
